import styled from 'styled-components';
import LikeButton from 'src/components/buttons/likeButton';
import GetElement from 'src/components/getElement';
import PostImage from 'src/modules/community/singlePost/components/postImage';
import i18n from 'src/utils/translate';
import { textLinkPrimary } from 'styles-js/mixins/typography';
import ActionsDropdown from 'src/modules/community/singlePost/components/actionsDropdown';
import LastEditedBy from 'src/modules/community/singlePost/components/lastEditedBy';
import Button from 'src/components/buttons/button';
import { useDispatch, useSelector } from 'src/store/store';
import { Permission, RestructuredReply } from 'src/constants/types';
import { Section, Element, Event } from 'src/constants/footfall';
import { userHasPermission } from 'src/utils/context';
import { trackFootfallEvent } from 'src/utils/footfall';
import { showModal, showSignupModal } from 'src/modules/shared/modal/actions';
import { ModalType } from 'src/constants/modalTypes';
import { ModalProps } from 'src/modules/shared/modal/components/report/reportModal';

type ReplyContentProps = {
  response: RestructuredReply;
  startEditing: () => void;
  startReplying: () => void;
}

export default function ReplyItemBody({ response, startEditing, startReplying }: ReplyContentProps): JSX.Element {
  const dispatch = useDispatch();

  const { currentUser, currentCommunity } = useSelector((state) => state.context);
  const { post } = useSelector((state) => state.pages.singlePost);
  const { communityId, isArchived } = currentCommunity;

  const onReplyClick = () => {
    if (!currentUser) {
      trackFootfallEvent(Event.CLICKED, {
        clickType: 'sign-up',
        clickMetadata: {
          section: Section.POST_REPLY,
          element: Element.REPLY,
        },
      });
      dispatch(showSignupModal());
      return;
    } else {
      startReplying();
    }
  };

  const onReportClick = () => {
    if (currentUser) {
      trackFootfallEvent(Event.CLICKED, {
        clickType: 'report',
        clickMetadata: { section: Section.Reply },
      });
      const modalProps: ModalProps = {
        reportType: 'post',
        postId: response.id,
        communitySlug: currentCommunity?.slug,
        footfallSection: Section.Reply,
      };
      dispatch(showModal({ modalType: ModalType.Report, modalProps }));
    } else {
      trackFootfallEvent(Event.CLICKED, {
        clickType: 'sign-up',
        clickMetadata: {
          element: Element.REPORT,
          section: Section.POST_REPLY,
        },
      });
      dispatch(showSignupModal());
    }
  };

  const canReport = userHasPermission(currentUser, Permission.SendReportAbuse);

  return (
    <>
      <GetElement enrichedContent={response.enriched.body} />
      <PostImage
        communityId={communityId}
        fullscreen={true}
        imageDescription={response.imageDescription}
        imagePreview={response.imagePreview}
        imageUrl={response.imageUrl}
      />
      {response.editAuthor && <LastEditedBy editAuthor={response.editAuthor} marginBottom={'20px'} />}
      <ReplyButtonsPanel>
        {post && !post.isLocked && !isArchived && (
          <Button
            className="reply"
            size="sm"
            onClick={onReplyClick}
          >
            {i18n.t('Reply')}
          </Button>
        )}
        <LikeButton
          noText={true}
          post={response}
          signupContext={{ section: Section.POST_REPLY }}
        />
        {(!currentUser || canReport) && (
          <ReportButton onClick={onReportClick}>
            {i18n.t('Report')}
          </ReportButton>
        )}
        {!!currentUser && (
          <ActionsDropdown
            buttonText={i18n.t('More')}
            dataTestId="reply-more-dropdown"
            post={response}
            superPost={post}
            onEdit={startEditing}
          />
        )}
      </ReplyButtonsPanel>
    </>
  );
}

const ReplyButtonsPanel = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  button {
    margin-right: 5px;
    @media (${({ theme }) => theme.overScreenSmall}) {
      margin-right: 10px;
    }
  }
`;

const ReportButton = styled.button`
  ${textLinkPrimary};
  background: none;
  border: none;
  padding: 6px 5px 6px 5px;
`;
